import ArrayMap from './ArrayMap';
import {EntityType} from '../@types/entity';

/**
 * Registry of ArrayMap templates initialized at app startup
 *
 * Each entity type has one template ArrayMap with schema preloaded.
 * When Redux needs a new instance, it clones from the template.
 * This avoids any circular dependencies and ensures schemas are always available.
 */
class ArrayMapRegistry {
    private static templates: Map<string, ArrayMap<any>> = new Map();

    /**
     * Register an ArrayMap template for an entity type
     * Called once per entity type at app startup
     */
    static register<T extends EntityType>(type: string, schema: any): void {
        const template = new ArrayMap<T>(type, [], schema);
        this.templates.set(type, template);
    }

    /**
     * Get a clone of the template for an entity type
     * Used by reducers to create new instances while preserving schema
     */
    static clone<T extends EntityType>(type: string, data: T[] = []): ArrayMap<T> {
        const template = this.templates.get(type);

        if (!template) {
            throw new Error(
                `ArrayMap template for '${type}' not found. ` +
                `Make sure ArrayMapRegistry.register() was called at app startup for this entity type.`
            );
        }

        // Start with a clone of the template (includes schema)
        const result = template.clone();

        // Add the data
        data.forEach(d => result.push(d));

        return result;
    }

    /**
     * Check if a template is registered (mainly for testing)
     */
    static has(type: string): boolean {
        return this.templates.has(type);
    }

    /**
     * Clear all templates (mainly for testing)
     */
    static clear(): void {
        this.templates.clear();
    }
}

export default ArrayMapRegistry;
